import { useSelector } from "react-redux";
import ReportType from "./ReportType";
import { JSXElementConstructor, ReactElement, ReactNode, ReactPortal, useEffect, useState } from "react";
import { DataTableColumn } from "../../../../components/common/datatable/DataTableTypes";
import { createClickableLink } from "../../../../components/common/datatable/DataTableUtils";
import { SearchInputWrapper } from "../../../../components/common/datatable/filter/SearchInputWrapper";
import { SearchSelectWithList } from "../../../../components/common/datatable/filter/SearchSelectWrapper";
import { formatAsCurrency } from "../../../../helpers/utils/TextUtils";
import { formatValueAsDateAndTime } from "../../../../helpers/utils/DateUtils";
import { SearchDatePickerWrapper } from "../../../../components/common/datatable/filter/SearchDatePickerWrapper";
import Breadcrumb from "../../../../components/common/Breadcrumbs";
import { DataTable } from "../../../../components/common/datatable/DataTable";
import { useLazyExportAccountsRecievableReportDataQuery, useSearchAccountsReceivableReportApiQuery } from "../report";
import { useLazyGetAllCustomerByTenantQuery } from "../../../customer/customerApi";
import { useGetAllTenantsQuery } from "../../../customer/tenantApi";
import { useLazyGetBranchByCustomerIdQuery } from "../../../customer/branchContactApi";
import { getCurrentMonthStartDate } from "../../../../helpers/utils/Utils";
import {useFindUserSettingsByGroupNameQuery} from "../../../../components/common/userSetting/userSettingsApi";



const AccountsReceivableReport = () => {

    const tenantData = useGetAllTenantsQuery("");
    const [customerData, customerApiRes] = useLazyGetAllCustomerByTenantQuery();
    const [branchData, branchDataApiRes] = useLazyGetBranchByCustomerIdQuery();
    const [type, setType] = useState('ACCOUNTS_RECEIVABLE_REPORT');
    const formattedStartDate = getCurrentMonthStartDate();
    const { data: userSetting } = useFindUserSettingsByGroupNameQuery("Report Accounts Receivable");

    const [crumbs] = useState([
        { content: "Home", url: "/home" },
        { content: "Accounts Receivable Report", url: "" }]
    )
    const [triggerExportReportDataQuery] = useLazyExportAccountsRecievableReportDataQuery();

    useEffect(() => {
        document.title = 'Accounts Receivable';
    }, []);


    const columns: DataTableColumn[] = [
        {
            label: "Customer Reference",
            accessor: "invoiceReference",
            visibility: { hasShowSetting: false },
            canSort: false,
            cell: (props: any) => {
                let path = "/orders/" + props.row.original.orderId;
                return createClickableLink(path, props.value, userSetting);
            },
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Customer Reference..."}
                    name={"customerOrderNumber"}
                    accessor={"customerOrderNumber"} />,
                overrideFilterAccessor: "customerOrderNumber"
            }]
        },
        {
            label: "Order Number",
            accessor: "orderNumber",
            visibility: { hasShowSetting: false },
            canSort: false,
            cell: (props: any) => {
                return props.value;
            },
        },

        {
            label: "Tenant",
            accessor: "tenant",
            overrideSortColumn: "tenant",
            visibility: { canColumnShow: true },
            filterOptions: [{
                dependentColumns: ["Customer"],
                filterComponent: SearchSelectWithList(tenantData?.data, (tenant: { name: any; }) => `${tenant.name}`, "name", "Select Tenant...", false, "tenant"),
                overrideFilterAccessor: "tenant",
            }],
        },

        {
            label: "Customer",
            accessor: "customer",
            overrideSortColumn: "customer_id",
            visibility: { hasShowSetting: false },
            canSort: false,
            cell: (props: any) => {
                return props.value;
            },
            filterOptions: [{
                dependentColumns: ["Branch(es)"],
                filterComponent: SearchSelectWithList(customerApiRes?.data, (customer: { customerName: any; customerNumber: any; }) => `${customer.customerName} (${customer.customerNumber})`, "id", "Select Customer...", false, "customerId"),
                onDependencyUpdated: (value: any) => {
                    customerData({ tenantName: value });
                },
                overrideFilterAccessor: "customerId",
            }],
        },

        {
            label: "Branch(es)",
            accessor: "branch",
            visibility: { hasShowSetting: false, canColumnShow: false },
            canSort: false,
            cell: (props: { value: string | number | boolean | ReactElement<any, string | JSXElementConstructor<any>> | Iterable<ReactNode> | ReactPortal | null | undefined; }) => {
                return <span>{props.value}</span>;
            },
            filterOptions: [{
                filterComponent: SearchSelectWithList(branchDataApiRes?.data?.data, "name", "id", "Select Branches...", true, "branchIds"),
                onDependencyUpdated: (value: any) => {
                    branchData(value);
                },
                overrideFilterAccessor: "branchIds",
                overrideFilterLabel: "Branch(es)",

            }]
        },

        {
            label: "Description",
            accessor: "description",
            overrideSortColumn: "description",
            visibility: { hasShowSetting: false },
            canSort: false,
            cell: (props: { value: any; }) => {
                return props.value;
            }
        },

        {
            label: "Amount",
            accessor: "amount",
            overrideSortColumn: "amount",
            visibility: { hasShowSetting: false },
            canSort: false,
            cell: (props: { value: any; }) => {
                return formatAsCurrency(props.value);
            }
        },

        {
            label: "Tax",
            accessor: "taxAmount",
            overrideSortColumn: "taxAmount",
            visibility: { hasShowSetting: false },
            canSort: false,
            cell: (props: { value: any; }) => {
                return formatAsCurrency(props.value);
            }
        },

        {
            label: "Total",
            accessor: "totalForOrder",
            overrideSortColumn: "totalForOrder",
            visibility: { hasShowSetting: false },
            canSort: false,
            cell: (props: { value: any; }) => {
                return formatAsCurrency(props.value);
            }
        },
        {
            label: "Date",
            visibility: { hasShowSetting: false },
            accessor: "firstDeliveredDate",
            overrideSortColumn: "creation_date",
            cell: (props: { row: { original: { firstDeliveredDate: any; }; }; }) => {
                return formatValueAsDateAndTime(props.row.original.firstDeliveredDate, 'MM/DD/YY h:mm a');
            },
            filterOptions: [{
                filterComponent: <SearchDatePickerWrapper accessor={"deliveredDateFrom"} />,
                overrideFilterAccessor: "deliveredDateFrom",
                overrideFilterLabel: "Delivered Date - From",
            }, {
                filterComponent: <SearchDatePickerWrapper accessor={"deliveredDateTo"} />,
                overrideFilterAccessor: "deliveredDateTo",
                overrideFilterLabel: "Delivered Date - To",
            }]
        }
    ];
    const customFilter = {
        deliveredDateFrom: formattedStartDate,
    }


    return (
        <>


            <div className="user-container px-4 py-2">
                <div className="flex justify-between">
                    <Breadcrumb crumbs={crumbs} />
                </div>
                <ReportType type={type} setType={setType} />
                <DataTable
                    columns={columns}
                    actions={{
                        fetchAction: useSearchAccountsReceivableReportApiQuery,
                        exportAction: triggerExportReportDataQuery
                    }
                    }
                    defaultSortOptions={{
                        accessor: "creation_date",
                        direction: "asc"
                    }}
                    defaultFilter={
                        {
                            ...customFilter,

                            "pageNumber": 0,
                            "pageSize": "15",

                        }
                    }
                    userSettingGroup={"Report Accounts Receivable"}
                    basePermission={"ACCRECREPO"}
                />
            </div>
            <div />

        </>
    )
}

export default AccountsReceivableReport;